import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getClientDetails, updateClientDetails, getFaxTemplates, getSensitiveFields } from "./ClientView.actions";
import { CLIENT_UPDATE_SENSITIVE_FIELDS } from "../../../lookup/ClientViewConstants";
import ConfirmationModal from "components/ConfirmationModal";
import Spinner from "components/Spinner";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import './ClientView.css';
import { useConfigForm } from "../configTool/useConfigForm";
import { validateClientEditForm, normalizePhoneDigits } from "../configTool/configFormValidators";
import {
  ConfigReadOnlyBanner,
  SensitiveFieldsConfirmationBody,
} from "../configTool/ConfigFormComponents";
import { ClientFormSections } from "../configTool/ClientFormSections";

const ClientView = () => {
  const { clientInfoUuid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    details: client,
    loadingDetails,
    updating,
    sensitiveFields,
  } = useSelector(
    (state) => state.clientView
  );

  const {
    faxTemplates,
    loadingFaxTemplates,
  } = useSelector((state) => state.customerView);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [changedSensitiveFields, setChangedSensitiveFields] = useState([]);

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);
  const isReadOnly = !canManage;

  const initialFormValues = useMemo(() => {
    if (!client) return null;
    return {
      ...client,
      supportNumber: normalizePhoneDigits(client.supportNumber),
      faxNumberInbound: normalizePhoneDigits(client.faxNumberInbound),
      prescriberCallActivitiesEnabled: client.prescriberCallActivitiesEnabled === true,
      pharmacyCallActivitiesEnabled: client.pharmacyCallActivitiesEnabled === true,
      useNvpSourcePricing: client.useNvpSourcePricing === true,
      excludeStarSwitches: client.excludeStarSwitches === true,
      excludeStarDeprescriptions: client.excludeStarDeprescriptions === true,
      polypharmacyEnabled: client.polypharmacyEnabled === true,
      deprescriptionConfirmationEnabled: client.deprescriptionConfirmationEnabled === true,
      isRxAdvocateEnabled: client.isRxAdvocateEnabled === true,
      language: client.language || '',
      populationType: client.populationType || '',
      faxTemplate: client.faxTemplate || '',
    };
  }, [client]);

  const {
    formState,
    originalFormState,
    errors,
    isDirty,
    handleInputChange,
    handleCheckboxChange,
    runValidation,
  } = useConfigForm({
    initialValues: initialFormValues,
    validate: validateClientEditForm,
  });

  useEffect(() => {
    dispatch(getClientDetails(clientInfoUuid));
    dispatch(getFaxTemplates());
    dispatch(getSensitiveFields(CLIENT_UPDATE_SENSITIVE_FIELDS));
  }, [dispatch, clientInfoUuid]);

  const handleModalConfirmSave = () => {
    setShowConfirmationModal(false);
    proceedWithSave();
  };

  const proceedWithSave = () => {
    dispatch(updateClientDetails(clientInfoUuid, formState)).then(
      (response) => {
        if (response && response.type === "UPDATE_CLIENT_DETAILS_SUCCESS") {
          navigate(-1);
        }
      }
    );
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSave = () => {
    setShowValidation(true);
    if (!runValidation()) {
      return;
    }

    const changedFields = sensitiveFields.filter(
      (field) => originalFormState && originalFormState[field] !== formState[field]
    );
    if (changedFields.length > 0) {
      setChangedSensitiveFields(changedFields);
      setShowConfirmationModal(true);
    } else {
      proceedWithSave();
    }
  };

  if (loadingDetails || loadingFaxTemplates || !client || !formState) {
    return <Spinner />;
  }

  return (
    <Container fluid>
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleModalConfirmSave}
        title="Confirm Save"
        body={<SensitiveFieldsConfirmationBody changedFields={changedSensitiveFields} />}
      />
      <Row>
        <Col md={12}>
          <h5>{client.clientName}</h5>
        </Col>
      </Row>
      {isReadOnly && (
        <Row className="mt-3">
          <Col md={12}>
            <ConfigReadOnlyBanner />
          </Col>
        </Row>
      )}
      <Row className="mt-4">
        <Col md={12}>
          <div className="client-details-wrapper">
            {updating && (
              <div className="updating-overlay">
                <Spinner />
              </div>
            )}
            <Form>
              <ClientFormSections
                formState={formState}
                errors={errors}
                onInputChange={handleInputChange}
                onCheckboxChange={handleCheckboxChange}
                faxTemplates={faxTemplates}
                loadingFaxTemplates={loadingFaxTemplates}
                isReadOnly={isReadOnly}
                sensitiveFields={sensitiveFields}
                showValidation={showValidation}
                disabledFields={{
                  clientName: true,
                  clientNameAbbreviation: true,
                  callActivities: true,
                }}
              />
            </Form>
          </div>
        </Col>
      </Row>
      {canManage && (
        <Row className="mt-4">
          <Col md={12} className="text-right">
            <Button variant="secondary" onClick={handleCancel} className="mr-2">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={updating || !isDirty} className="save-button">
              Save
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ClientView;
